import { MODULE_CONTENT_MAP } from '@attackiq/constants/platform';

export const ScenarioType = {
  Attack: 1,
  Validation: 2
} as const;

export type ScenarioType = (typeof ScenarioType)[keyof typeof ScenarioType];

export interface ProjectTemplateTestScenario {
  id: string;
  project_template_name: string;
  project_template_project_name: string;
  scenario: Scenario;
}

export interface ProjectTemplateTest {
  id: string;
  name: string;
  description: string;
  project_template_name: string;
  project_template_project_name: string;
  scenarios: string[];
  project_template_test_scenarios?: ProjectTemplateTestScenario[];
}

export interface Scenario {
  id: string;
  tracker_id: string;
  second_id: string;
  name: string;
  description: string;
  scenario_template: string;
  model_json: Record<string, unknown>;
  descriptor_json: null;
  extras: null;
  scenario_type: number;
  runnable: boolean;
  supported_platforms: SupportedPlatforms;
  company: string;
  user: null;
  created: string;
  modified: string;
  user_type: string;
  time_to_live: number;
  tags: Tag[];
  cancellable: boolean;
  cloud_providers: unknown[];
  is_multi_asset: boolean;
  indicator_pattern: string;
  indicator_source: null;
  parameters_description: null;
  mitigations: unknown[];
  modules: string[];
  module_contents: string[];
  last_updated: string;
}

export interface SupportedPlatforms {
  osx: string;
  amazon: string;
  centos: string;
  debian: string;
  redhat: string;
  ubuntu: string;
  windows: string;
}

export interface ProjectTemplate {
  id: string;
  vendor_product_id: null | string;
  modules: string[];
  module_contents: string[];
  created: string;
  modified: string;
  syncd_on: string;
  last_updated: string;
  template_name: string;
  template_description: string;
  project_name: string;
  project_description: string;
  project_id: null;
  icon: string;
  default_schedule: null;
  meta_data: MetaData;
  company: string;
  project_template_type: string;
  user_type: string;
  report_types: string[];
  widgets: string[];
  attack_graph_templates: string[];
}

export interface MetaData {
  behavior: string;
  category: string;
  assessment_type: string;
}

export type BlueprintTemplate = {
  id: string;
  name: string;
};

export type AttackGraphTemplate = {
  id: string;
  name: string;
};

// We only support equality filters
export type AttackIQFilter = {
  field: string;
  value: string | number;
};

export type ScenarioUnifiedMitigation = {
  id?: string;
  scenario: string;
  unifiedmitigation: string;
  unifiedmitigationtype: string;
  order: number;
};

export type ScenarioMitigation = {
  scenario: string;
  name: string;
  description: string;
  id: string;
  order: number;
  vendor_product: string | null;
};

export type Tag = {
  id: string;
  display_name: string;
  name: string;
  description: string;
  tag_set_id: string;
  tag_set_name: string;
};

export interface TaggedItem {
  id: string;
  tag_id: string;
  tag_display_name: string;
  tag_name: string;
  tag_set_id: string;
  tag_set_name: string;
  content_type: number;
  content_type_model: string;
  object_id: string;
  content_object: string;
  company: string;
}

export type VendorProductTag = {
  id: string;
  vendor_name: string;
  vendor_product: string;
  vendor_product_name: string;
  tag: string;
  tag_name: string;
  tag_display_name: string;
};

export type Module = {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
};

export type New = {
  active: boolean;
  company: string;
  content_id: string;
  content_type_name: string | null;
  cover_image: string;
  created: string;
  date: string;
  description: string;
  featured: boolean;
  id: string;
  modified: string;
  modules: string[];
  object_fingerprint: string;
  scenario_tag_ids: string[];
  syncd_on: string | null;
  title: string;
  type: 'whats_new' | 'emerging_threat' | 'release_note';
  url: string;
  order: number;
  show_in_enterprise: boolean;
  show_in_ready: boolean;
  show_in_flex: boolean;
};

export type ThreatInformedDefense = {
  active: boolean;
  company: string;
  cover_image: string;
  created: string;
  date: string;
  description: string;
  highlight: boolean;
  id: string;
  modified: string;
  object_fingerprint: string;
  syncd_on: string | null;
  title: string;
  url: string;
  show_in_enterprise: boolean;
  show_in_ready: boolean;
  show_in_flex: boolean;
};

export type ModuleContent = {
  id: string;
  module_id: string;
  content_id: string;
  content_object: {
    id: string;
    name?: string;
    description?: string;
    template_name?: string;
    template_description?: string;
  };
  content_type_model: keyof typeof MODULE_CONTENT_MAP;
  content_type: number;
  content_type_app_label: 'ai_cloud' | string;
};

export interface Phase {
  id: string;
}

export interface Mitigation {
  id: string;
}

export interface Stage {
  id: string;
}

export interface VendorProduct {
  id: string;
}

export interface VendorProductDomain {
  id: string;
}

export interface FlexPackage {
  id: string;
  name: string;
  os?: string;
  arch?: string;
}

export interface HistoryChange {
  field: string;
  new: string | Record<PropertyKey, unknown>;
  old: string | Record<PropertyKey, unknown>;
}

export interface UnifiedMitigation {
  id: number;
  name: string;
  unifiedmitigationtype: number;
  unified_mitigation_type_id: string;
  description: string | null;
  content: string;
  reference_id: string;
  source: string;
  source_type: string;
  severity: string;
  stage: string;
  metadata: object;
  is_active: boolean;
  company: string;
  tags: string[];
  parent_rule_id: string | null;
  parent_rule_name: string | null;
  created: string;
  modified: string;
  notes_count: number;
}

export interface UnifiedMitigationType {
  id: number;
  name: string;
}
